import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { AppHeader } from './AppHeader';
import { Button } from "@/components/ui/button";
import {
  Building2, User, Video, FileText, Clock,
  Settings, Calendar, Mail, Loader2, Lock,
  Users, Shield, Briefcase, Construction,
  Phone, Check, UserPlus, Eye, EyeOff,
  Copy, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import './AdminPanel.css';
import './ManagerPanel.css';

const API = process.env.REACT_APP_BACKEND_URL;

const agencyStyleOptions = [
  { value: 'Friendly & Conversational', description: 'Casual, approachable tone.' },
  { value: 'Premium & Polished', description: 'Sophisticated, luxury feel.' },
  { value: 'Straight-Talking & Confident', description: 'Direct and no-nonsense.' },
  { value: 'Warm & Reassuring', description: 'Comforting and supportive tone.' },
  { value: 'Clean & Clear', description: 'No fluff, just the facts.' }
];

function ManagerPanel({ onBack }) {
  const { user } = useAuth();
  const role = user?.role || '';

  const [loading, setLoading] = useState(true);
  const [myBranch, setMyBranch] = useState(null);
  const [agents, setAgents] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [noBranch, setNoBranch] = useState(false);

  // WMS Agent onboarding state
  const [wmsAgents, setWmsAgents] = useState([]);
  const [wmsLoading, setWmsLoading] = useState(false);
  const [selectedWmsIds, setSelectedWmsIds] = useState([]);
  const [onboarding, setOnboarding] = useState(false);
  const [onboardResult, setOnboardResult] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    if (role === 'branch_manager') {
      loadBranchData();
    } else {
      setLoading(false);
    }
  }, [role]);

  const loadBranchData = async () => {
    setLoading(true);
    try {
      const [branchRes, agentsRes, avatarsRes] = await Promise.all([
        axios.get(`${API}/api/admin/my-branch`),
        axios.get(`${API}/api/admin/agents`),
        axios.get(`${API}/api/admin/avatars`)
      ]);
      setMyBranch(branchRes.data);
      setAgents(agentsRes.data);
      setAvatars(avatarsRes.data);

      // Auto-load WMS agents if branch has wms_branch_id
      if (branchRes.data?.id) {
        loadWmsAgents(branchRes.data.id);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setNoBranch(true);
      } else {
        toast.error('Failed to load branch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadWmsAgents = async (branchId) => {
    setWmsLoading(true);
    try {
      const response = await axios.get(`${API}/api/wms/branch-agents/${branchId}`);
      setWmsAgents(response.data.agents || []);
    } catch (error) {
      // If branch isn't linked to WMS, silently ignore
      if (error.response?.status !== 400) {
        console.error('Failed to load WMS agents:', error);
      }
    } finally {
      setWmsLoading(false);
    }
  };

  const toggleWmsSelection = (wmsId) => {
    setSelectedWmsIds(prev =>
      prev.includes(wmsId) ? prev.filter(id => id !== wmsId) : [...prev, wmsId]
    );
  };

  const handleOnboard = async () => {
    if (selectedWmsIds.length === 0) {
      toast.error('Select at least one agent to onboard');
      return;
    }
    setOnboarding(true);
    setOnboardResult(null);
    try {
      const response = await axios.post(`${API}/api/wms/onboard-agents`, {
        branch_id: myBranch.id,
        wms_user_ids: selectedWmsIds,
      });
      setOnboardResult(response.data);
      setSelectedWmsIds([]);
      toast.success(`Onboarded ${response.data.total_onboarded} agent(s)`);
      // Reload agents
      loadWmsAgents(myBranch.id);
      loadBranchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to onboard agents');
    } finally {
      setOnboarding(false);
    }
  };

  const copyPassword = (password) => {
    navigator.clipboard.writeText(password);
    toast.success('Password copied');
  };

  const handleUpdateBranchStyle = async (newStyle) => {
    if (!myBranch) return;
    try {
      await axios.patch(`${API}/api/admin/branches/${myBranch.id}`, {
        agency_style: newStyle
      });
      setMyBranch(prev => ({ ...prev, agency_style: newStyle }));
      toast.success('Agency style updated');
    } catch (error) {
      toast.error('Failed to update agency style');
    }
  };

  const isAvatarAssigned = (avatarId) => agents.some(a => a.avatar_id === avatarId);
  const getAgentForAvatar = (avatarId) => agents.find(a => a.avatar_id === avatarId);

  const getRoleLabel = () => {
    switch (role) {
      case 'branch_manager': return 'Branch Manager';
      case 'brand_manager': return 'Brand Manager';
      case 'group_manager': return 'Group Manager';
      case 'founder': return 'Founder';
      case 'md': return 'Managing Director';
      default: return 'Manager';
    }
  };

  // Split WMS agents into onboarded and available
  const onboardedAgents = wmsAgents.filter(a => a.onboarded);
  const availableAgents = wmsAgents.filter(a => !a.onboarded && !a.disabled);
  const disabledAgents = wmsAgents.filter(a => a.disabled && !a.onboarded);

  if (loading) {
    return (
      <div className="admin-loading">
        <Loader2 className="animate-spin" size={32} />
        <span>Loading management panel...</span>
      </div>
    );
  }

  return (
    <div className="admin-panel" data-testid="manager-panel">
      <AppHeader onBack={onBack} />
      <div className="admin-header">
        <h1 className="admin-title">
          <Settings size={24} />
          Management
        </h1>
        <span className="admin-role">{getRoleLabel()}</span>
      </div>

      <div className="admin-content">
        {/* BRANCH MANAGER — Full branch view */}
        {role === 'branch_manager' && !noBranch && myBranch && (
          <div className="admin-section" data-testid="branch-manager-section">
            {/* Branch Info Card */}
            <div className="manager-branch-card" data-testid="branch-info-card">
              <div className="manager-branch-header">
                <Building2 size={22} />
                <div>
                  <h2>{myBranch.name}</h2>
                  {myBranch.address && <span className="branch-address">{myBranch.address}</span>}
                </div>
              </div>
              <div className="manager-branch-stats">
                <div className="stat-pill"><Users size={14} /> {myBranch.agent_count} agents</div>
                <div className="stat-pill"><Video size={14} /> {myBranch.avatar_count} avatars</div>
                {onboardedAgents.length > 0 && (
                  <div className="stat-pill"><CheckCircle size={14} /> {onboardedAgents.length} onboarded</div>
                )}
              </div>
              <div className="manager-style-row">
                <label className="manager-style-label">Agency Style</label>
                <select
                  className="style-select enhanced"
                  value={myBranch.agency_style || 'Friendly & Conversational'}
                  onChange={(e) => handleUpdateBranchStyle(e.target.value)}
                  data-testid="branch-style-select"
                >
                  {agencyStyleOptions.map(s => (
                    <option key={s.value} value={s.value}>{s.value}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* WMS Agent Onboarding */}
            {wmsAgents.length > 0 && (
              <div className="wms-agents-section" data-testid="wms-agents-section">
                <div className="section-header">
                  <h2>Agents</h2>
                </div>

                {/* Onboarded Agents */}
                {onboardedAgents.length > 0 && (
                  <div className="agents-group" data-testid="onboarded-agents">
                    <h3 className="agents-group-title">
                      <CheckCircle size={16} /> Active on System ({onboardedAgents.length})
                    </h3>
                    <div className="agents-list">
                      {onboardedAgents.map(agent => (
                        <div key={agent.wms_id} className="agent-row onboarded" data-testid={`agent-${agent.wms_id}`}>
                          {agent.avatar_url && (
                            <img src={agent.avatar_url} alt="" className="agent-avatar" />
                          )}
                          {!agent.avatar_url && (
                            <div className="agent-avatar-placeholder"><User size={16} /></div>
                          )}
                          <div className="agent-info">
                            <span className="agent-name">{agent.name}</span>
                            <span className="agent-email">{agent.email}</span>
                            {agent.system_phone && (
                              <span className="agent-phone"><Phone size={10} /> {agent.system_phone}</span>
                            )}
                          </div>
                          <span className="agent-badge active">Active</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Agents to Onboard */}
                {availableAgents.length > 0 && (
                  <div className="agents-group" data-testid="available-agents">
                    <h3 className="agents-group-title">
                      <UserPlus size={16} /> Available to Onboard ({availableAgents.length})
                    </h3>
                    <div className="agents-list">
                      {availableAgents.map(agent => {
                        const isSelected = selectedWmsIds.includes(agent.wms_id);
                        return (
                          <div
                            key={agent.wms_id}
                            className={`agent-row selectable ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleWmsSelection(agent.wms_id)}
                            data-testid={`agent-select-${agent.wms_id}`}
                          >
                            {agent.avatar_url && (
                              <img src={agent.avatar_url} alt="" className="agent-avatar" />
                            )}
                            {!agent.avatar_url && (
                              <div className="agent-avatar-placeholder"><User size={16} /></div>
                            )}
                            <div className="agent-info">
                              <span className="agent-name">{agent.name}</span>
                              <span className="agent-email">{agent.email}</span>
                            </div>
                            <div className={`agent-checkbox ${isSelected ? 'checked' : ''}`}>
                              {isSelected && <Check size={14} />}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {selectedWmsIds.length > 0 && (
                      <button
                        className="onboard-btn"
                        onClick={handleOnboard}
                        disabled={onboarding}
                        data-testid="onboard-btn"
                      >
                        {onboarding ? (
                          <><Loader2 size={14} className="animate-spin" /> Onboarding...</>
                        ) : (
                          <><UserPlus size={14} /> Onboard {selectedWmsIds.length} Selected Agent{selectedWmsIds.length > 1 ? 's' : ''}</>
                        )}
                      </button>
                    )}
                  </div>
                )}

                {/* Disabled agents (collapsed) */}
                {disabledAgents.length > 0 && (
                  <div className="agents-group disabled-group">
                    <h3 className="agents-group-title disabled">
                      Inactive in WMS ({disabledAgents.length})
                    </h3>
                  </div>
                )}
              </div>
            )}

            {/* Onboard Result — show credentials */}
            {onboardResult && onboardResult.onboarded.length > 0 && (
              <div className="onboard-result" data-testid="onboard-result">
                <div className="onboard-result-header">
                  <CheckCircle size={20} />
                  <h3>Agents Onboarded Successfully</h3>
                </div>
                <p className="onboard-result-note">
                  Save these temporary passwords — they won't be shown again. 
                  Agents will need to add their mobile number for WhatsApp delivery.
                </p>
                <div className="credentials-list">
                  {onboardResult.onboarded.map(agent => (
                    <div key={agent.id} className="credential-row" data-testid={`credential-${agent.wms_id}`}>
                      <div className="credential-info">
                        <span className="credential-name">{agent.name}</span>
                        <span className="credential-email">{agent.email}</span>
                      </div>
                      <div className="credential-password">
                        <code>
                          {showPasswords[agent.id] ? agent.temp_password : '••••••••'}
                        </code>
                        <button
                          className="pwd-toggle"
                          onClick={() => setShowPasswords(prev => ({ ...prev, [agent.id]: !prev[agent.id] }))}
                        >
                          {showPasswords[agent.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          className="pwd-copy"
                          onClick={() => copyPassword(agent.temp_password)}
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Avatars */}
            {avatars.length > 0 && (
              <>
                <div className="section-header" style={{ marginTop: 24 }}>
                  <h2>Avatars</h2>
                </div>
                <div className="items-list">
                  {avatars.map((avatar) => {
                    const assigned = isAvatarAssigned(avatar.id);
                    const agent = getAgentForAvatar(avatar.id);
                    return (
                      <div key={avatar.id} className={`item-card ${!assigned ? 'avatar-available' : ''}`} data-testid={`avatar-card-${avatar.id}`}>
                        <div className={`item-icon avatar-icon-box ${!assigned ? 'available' : ''}`}>
                          <Video size={20} />
                        </div>
                        <div className="item-info">
                          <span className="item-name">{avatar.agent_name}</span>
                          <span className="item-meta">ID: {avatar.avatar_id}</span>
                          {assigned ? (
                            <span className="avatar-status assigned">
                              <User size={10} /> {agent?.name} ({agent?.email})
                            </span>
                          ) : (
                            <span className="avatar-status available">Available</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* No WMS agents */}
            {wmsAgents.length === 0 && !wmsLoading && avatars.length === 0 && (
              <div className="empty-state">
                <Users size={48} />
                <p>No agents or avatars found</p>
                <span>Contact your admin to set up agents and avatars</span>
              </div>
            )}
          </div>
        )}

        {/* BRANCH MANAGER — No branch assigned */}
        {role === 'branch_manager' && noBranch && (
          <div className="admin-section">
            <div className="manager-placeholder" data-testid="no-branch-assigned">
              <Building2 size={48} className="placeholder-icon" />
              <h3>No Branch Assigned</h3>
              <p>You haven't been assigned to a branch yet. Please contact your Super Admin to get assigned.</p>
            </div>
          </div>
        )}

        {/* BRAND MANAGER — Coming Soon */}
        {role === 'brand_manager' && (
          <div className="admin-section">
            <div className="manager-placeholder" data-testid="brand-manager-placeholder">
              <Briefcase size={48} className="placeholder-icon" />
              <h3>Brand Management</h3>
              <p className="placeholder-desc">As a Brand Manager, you'll be able to:</p>
              <ul className="placeholder-features">
                <li><Building2 size={14} /> View and manage all branches under your brand</li>
                <li><Users size={14} /> Monitor agent activity across branches</li>
                <li><FileText size={14} /> Review script output and quality</li>
                <li><Settings size={14} /> Set brand-level agency style defaults</li>
              </ul>
              <div className="coming-soon-badge">
                <Construction size={16} />
                Coming Soon
              </div>
            </div>
          </div>
        )}

        {/* GROUP MANAGER — Coming Soon */}
        {role === 'group_manager' && (
          <div className="admin-section">
            <div className="manager-placeholder" data-testid="group-manager-placeholder">
              <Shield size={48} className="placeholder-icon" />
              <h3>Group Management</h3>
              <p className="placeholder-desc">As a Group Manager, you'll be able to:</p>
              <ul className="placeholder-features">
                <li><Briefcase size={14} /> Oversee all brands in your group</li>
                <li><Building2 size={14} /> Monitor branch performance across brands</li>
                <li><Users size={14} /> View user activity and script output</li>
                <li><Settings size={14} /> Set group-level defaults and policies</li>
              </ul>
              <div className="coming-soon-badge">
                <Construction size={16} />
                Coming Soon
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ManagerPanel;
